import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";

const NAV_ITEMS = [
  {
    label: "Overview",
    href: "#overview",
  },
  {
    label: "Benefits",
    href: "#benefits",
  },
  {
    label: "Solutions",
    href: "#types",
  },
  {
    label: "Eligibility",
    href: "#eligibility",
  },
  {
    label: "Process",
    href: "#process",
  },
  {
    label: "Why Money Mart",
    href: "#why",
  },
  {
    label: "FAQs",
    href: "#faq",
  },
  {
    label: "Contact",
    href: "#lead-form",
  },
];

const NAVBAR_OFFSET = window.innerWidth >= 768 ? 80 : 64;

export default function LoanNavbar({ onCtaClick }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  const menuButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const id = location.hash.replace(/^#/, "");

    if (!id) {
      return;
    }

    const section = document.getElementById(id);

    if (!section) {
      return;
    }

    setActiveSection(id);

    const timer = window.setTimeout(() => {
      const top =
        section.getBoundingClientRect().top + window.scrollY - NAVBAR_OFFSET;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: "smooth",
      });
    }, 100);

    return () => {
      window.clearTimeout(timer);
    };
  }, [location.hash]);

  /*
   * Lock body scroll while mobile navigation is open.
   */
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  /*
   * Close mobile navigation whenever the route changes.
   */
  useEffect(() => {
    setOpen(false);
  }, [location.pathname], location.hash);

  /*
   * Close mobile navigation with Escape.
   */
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  /*
   * Close mobile navigation when clicking outside it.
   */
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleOutsideClick = (event) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open]);

  /*
   * Header background + active section detection.
   */
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;

      setScrolled(scrollY > 20);

      let currentSection = "";

      NAV_ITEMS.forEach((item) => {
        const id = item.href.substring(1);
        const section = document.getElementById(id);

        if (!section) {
          return;
        }

        const sectionTop =
          section.getBoundingClientRect().top +
          window.scrollY -
          NAVBAR_OFFSET -
          20;

        if (scrollY >= sectionTop) {
          currentSection = id;
        }
      });

      setActiveSection(currentSection);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /*
   * Smooth scroll to a section on the current loan page.
   */
  const scrollToSection = (href) => {
    const id = href.replace(/^#/, "");
    const section = document.getElementById(id);

    if (!section) {
      console.warn(`[LoanNavbar] Section not found: #${id}`);
      return;
    }

    // Update the URL hash without navigating away from the current loan page.
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: `#${id}`,
      },
      {
        replace: false,
      },
    );

    setActiveSection(id);

    // Close mobile menu.
    setOpen(false);

    // Wait for the menu state/body overflow to update before scrolling.
    requestAnimationFrame(() => {
      const top =
        section.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: "smooth",
      });
    });
  };

  const handleNavClick = (event, href) => {
    event.preventDefault();
    scrollToSection(href);
  };

  const handleEligibilityClick = () => {
    setOpen(false);

    if (onCtaClick) {
      onCtaClick();
      return;
    }

    scrollToSection("#lead-form");
  };

  return (
    <header
      data-testid="loan-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-gray-200/60 bg-white/90 backdrop-blur-xl"
          : "bg-white"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between md:h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="loan-header-logo"
            className="flex shrink-0 items-center gap-2"
            onClick={() => setOpen(false)}
          >
            <img
              src="/Logo.webp"
              alt="Money Mart"
              className="h-[60px] w-auto object-contain"
            />
          </Link>

          {/* Desktop navigation */}
          <nav
            aria-label="Loan page navigation"
            className="hidden items-center gap-7 lg:flex"
          >
            {NAV_ITEMS.map((item) => {
              const id = item.href.substring(1);
              const isActive = activeSection === id;

              return (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(event) => handleNavClick(event, item.href)}
                  className={`relative py-1 text-sm font-medium transition-colors ${
                    isActive
                      ? "font-semibold text-[#0F2D52]"
                      : "text-gray-700 hover:text-[#0F2D52]"
                  }`}
                >
                  {item.label}

                  {isActive && (
                    <span className="absolute inset-x-0 -bottom-1 h-0.5 rounded-full bg-[#D4AF37]" />
                  )}
                </a>
              );
            })}
          </nav>

          {/* CTA + mobile menu */}
          <div className="flex items-center gap-2">
            <Button
              data-testid="loan-header-cta"
              onClick={handleEligibilityClick}
              className="hidden h-10 rounded-full bg-[#0F2D52] px-5 text-sm font-semibold text-white hover:bg-[#0a1f38] sm:inline-flex"
            >
              Check Eligibility
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>

            <button
              ref={menuButtonRef}
              type="button"
              aria-expanded={open}
              aria-controls="loan-mobile-navigation"
              aria-label={
                open ? "Close navigation menu" : "Open navigation menu"
              }
              className="rounded-lg p-2 transition-colors hover:bg-gray-100 lg:hidden"
              onClick={() => setOpen((value) => !value)}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile navigation */}
      <AnimatePresence>
        {open && (
          <motion.div
            id="loan-mobile-navigation"
            ref={mobileMenuRef}
            initial={{
              opacity: 0,
              height: 0,
            }}
            animate={{
              opacity: 1,
              height: "auto",
            }}
            exit={{
              opacity: 0,
              height: 0,
            }}
            transition={{
              duration: 0.2,
            }}
            className="overflow-hidden border-b border-gray-200 bg-white lg:hidden"
          >
            <nav
              aria-label="Mobile loan page navigation"
              className="mx-auto flex max-w-7xl flex-col px-4 py-4 sm:px-6"
            >
              {NAV_ITEMS.map((item) => {
                const id = item.href.substring(1);
                const isActive = activeSection === id;

                return (
                  <button
                    key={item.href}
                    type="button"
                    onClick={() => scrollToSection(item.href)}
                    className={`w-full rounded-lg px-3 py-3 text-left text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-[#0F2D52]/5 text-[#0F2D52]"
                        : "text-gray-700 hover:bg-gray-50 hover:text-[#0F2D52]"
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}

              <Button
                onClick={handleEligibilityClick}
                className="mt-3 h-11 rounded-full bg-[#0F2D52] text-white hover:bg-[#0a1f38]"
              >
                Check Eligibility
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
